/**
 * WeddingContext — thin composer over the decomposed wedding hooks.
 *
 * Phase B.5 refactor: the 322-LOC monolith was split into focused hooks
 * under `./wedding/`. This file wires them into a single provider and
 * exposes exactly the same public `useWedding()` surface as before — no
 * call sites need to change.
 *
 * Component-level hooks (`useSeating`, `useTimeline`) are intentionally
 * NOT composed into the global context; they manage per-event state that
 * shouldn't be cached globally. Import them directly where needed.
 */
import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

import { useWeddingCrud } from './wedding/useWeddingCrud';
import { useEvents } from './wedding/useEvents';
import { useExpenses } from './wedding/useExpenses';
import { useVendors } from './wedding/useVendors';
import { useGuests } from './wedding/useGuests';
import { useChecklist } from './wedding/useChecklist';
import { useDashboard } from './wedding/useDashboard';
import { useMetadata } from './wedding/useMetadata';

const WeddingContext = createContext(null);

export function WeddingProvider({ children }) {
  const { user } = useAuth();

  const weddingCrud = useWeddingCrud();
  const eventsHook = useEvents();
  const expensesHook = useExpenses();
  const vendorsHook = useVendors();
  const guestsHook = useGuests();
  const checklistHook = useChecklist();
  const dashboardHook = useDashboard();
  const metadataHook = useMetadata();

  const { fetchEvents } = eventsHook;
  const { fetchExpenses } = expensesHook;
  const { fetchVendors } = vendorsHook;
  const { fetchGuests } = guestsHook;
  const { fetchChecklist } = checklistHook;
  const { fetchDashboard } = dashboardHook;
  const { applyTemplate: applyTemplateRaw } = metadataHook;
  const {
    weddings, currentWedding, setCurrentWedding,
    loading: weddingLoading, initialised: weddingsInitialised,
    fetchWeddings, createWedding, updateWedding, deleteWedding,
  } = weddingCrud;

  // Wrapper so applyTemplate still refreshes events/checklist/dashboard
  const applyTemplate = async (weddingId, templateId) => {
    const data = await applyTemplateRaw(weddingId, templateId);
    await Promise.all([
      fetchEvents(weddingId),
      fetchChecklist(weddingId),
      fetchDashboard(weddingId),
    ]);
    return data;
  };

  // Select wedding and fetch all related data (legacy selectWedding API)
  const selectWedding = async (wedding) => {
    setCurrentWedding(wedding);
    if (wedding) {
      await Promise.all([
        fetchEvents(wedding.id),
        fetchExpenses(wedding.id),
        fetchVendors(wedding.id),
        fetchGuests(wedding.id),
        fetchChecklist(wedding.id),
        fetchDashboard(wedding.id),
      ]);
    }
  };

  // Auto-fetch weddings on mount
  useEffect(() => {
    if (user && weddings.length === 0) {
      fetchWeddings();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // When currentWedding changes, fan out to every domain fetch
  useEffect(() => {
    if (currentWedding?.id) {
      fetchEvents(currentWedding.id);
      fetchExpenses(currentWedding.id);
      fetchVendors(currentWedding.id);
      fetchGuests(currentWedding.id);
      fetchChecklist(currentWedding.id);
      fetchDashboard(currentWedding.id);
    }
  }, [currentWedding?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading is true while either the weddings list or the dashboard is loading
  const loading = weddingLoading || dashboardHook.loading;

  // NOTE: the shape below mirrors the pre-refactor context exactly.
  // Do NOT reorder or rename keys — many pages destructure specific fields.
  const value = {
    weddings,
    currentWedding,
    weddingsInitialised,
    events: eventsHook.events,
    expenses: expensesHook.expenses,
    vendors: vendorsHook.vendors,
    guests: guestsHook.guests,
    checklist: checklistHook.checklist,
    dashboard: dashboardHook.dashboard,
    loading,

    currencies: metadataHook.currencies,
    categories: metadataHook.categories,
    templates: metadataHook.templates,

    fetchCategories: metadataHook.fetchCategories,
    fetchCurrencies: metadataHook.fetchCurrencies,
    fetchTemplates: metadataHook.fetchTemplates,
    applyTemplate,

    fetchWeddings,
    createWedding,
    updateWedding,
    deleteWedding,
    selectWedding,
    setCurrentWedding,

    fetchEvents,
    createEvent: eventsHook.createEvent,
    updateEvent: eventsHook.updateEvent,
    deleteEvent: eventsHook.deleteEvent,

    fetchExpenses,
    createExpense: expensesHook.createExpense,
    updateExpense: expensesHook.updateExpense,
    deleteExpense: expensesHook.deleteExpense,

    fetchVendors,
    createVendor: vendorsHook.createVendor,
    updateVendor: vendorsHook.updateVendor,
    deleteVendor: vendorsHook.deleteVendor,

    fetchGuests,
    createGuest: guestsHook.createGuest,
    updateGuest: guestsHook.updateGuest,
    deleteGuest: guestsHook.deleteGuest,

    fetchChecklist,
    createChecklistItem: checklistHook.createChecklistItem,
    updateChecklistItem: checklistHook.updateChecklistItem,
    deleteChecklistItem: checklistHook.deleteChecklistItem,

    fetchDashboard,
  };

  return (
    <WeddingContext.Provider value={value}>{children}</WeddingContext.Provider>
  );
}

export function useWedding() {
  const context = useContext(WeddingContext);
  if (!context) {
    throw new Error('useWedding must be used within WeddingProvider');
  }
  return context;
}

// Re-export focused hooks for pages that want scoped, stateful access.
// These are safe to call outside the provider because they manage their own
// state (useSeating + useTimeline). The others rely on the provider.
export { useSeating } from './wedding/useSeating';
export { useTimeline } from './wedding/useTimeline';
